/**
 * useCreateProjectFromGallery Hook
 *
 * @description React hook for generating project pages from galleries.
 * Handles the full flow: API call, loading states, polling, and result handling.
 *
 * @see /src/features/gallery/api/projectWriterApi.ts
 */

import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/use-auth'
import {
  generateProjectPage,
  getAgentStatus,
  type GenerateProjectResponse,
  type AgentStatusResponse,
} from '../api/projectWriterApi'

export interface UseCreateProjectFromGalleryOptions {
  /** Callback when generation starts */
  onStart?: () => void
  /** Callback when generation completes successfully */
  onSuccess?: (result: GenerateProjectResponse) => void
  /** Callback when generation fails */
  onError?: (error: string) => void
  /** Callback for progress updates during polling */
  onProgress?: (status: AgentStatusResponse) => void
}

export interface GenerateOptions {
  /** Use Responses API instead of Chat Completions (default: true) */
  useResponsesApi?: boolean
  /** Auto-enrich if not already done (default: true) */
  autoEnrich?: boolean
  /** Max images to include (default: 8) */
  imageCount?: number
  /** Overwrite an existing project page for this gallery (default: false) */
  overwriteExisting?: boolean
  /** Run a pre-brief extraction step before writing (default: true) */
  prepareBrief?: boolean
  /** Max photo captions to include in the brief step (default: 8) */
  briefImageCount?: number
  /** Run a style/editor pass after drafting (default: true) */
  editorPass?: boolean
}

export interface UseCreateProjectFromGalleryResult {
  /** Trigger project page generation */
  generate: (galleryId: number, options?: GenerateOptions) => Promise<GenerateProjectResponse>
  /** Whether generation is in progress */
  isLoading: boolean
  /** Current status during generation */
  status: AgentStatusResponse | null
  /** Agent ID for this run (used for status/logging) */
  agentId: string | null
  /** Correlates to Cloudflare logs (X-Request-Id header) */
  requestId: string | null
  /** Progress percentage (0-100) */
  progress: number
  /** Error message if generation failed */
  error: string | null
  /** Reset the hook state */
  reset: () => void
}

/**
 * Hook for creating project pages from galleries
 *
 * @example
 * ```tsx
 * function GalleryActions({ galleryId }) {
 *   const { generate, isLoading, progress, error } = useCreateProjectFromGallery({
 *     onSuccess: (result) => {
 *       toast.success(`Created project page: ${result.slug}`)
 *       navigate(`/project-pages/${result.project_page_id}`)
 *     },
 *     onError: (error) => {
 *       toast.error(error)
 *     }
 *   })
 *
 *   return (
 *     <Button onClick={() => generate(galleryId)} disabled={isLoading}>
 *       {isLoading ? `Creating... ${progress}%` : 'Create Project Page'}
 *     </Button>
 *   )
 * }
 * ```
 */
export function useCreateProjectFromGallery(
  options: UseCreateProjectFromGalleryOptions = {}
): UseCreateProjectFromGalleryResult {
  const { onStart, onSuccess, onError, onProgress } = options
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<AgentStatusResponse | null>(null)
  const [agentId, setAgentId] = useState<string | null>(null)
  const [requestId, setRequestId] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const reset = useCallback(() => {
    setStatus(null)
    setAgentId(null)
    setRequestId(null)
    setProgress(0)
    setError(null)
  }, [])

  const mutation = useMutation({
    mutationFn: async ({
      galleryId,
      options,
    }: {
      galleryId: number
      options?: GenerateOptions
    }): Promise<GenerateProjectResponse> => {
      if (!user?.id) {
        throw new Error('You must be logged in to create project pages')
      }

      onStart?.()
      setProgress(0)
      setError(null)

      // Convert options to API format
      const apiOptions = options
        ? {
            auto_enrich: options.autoEnrich,
            image_count: options.imageCount,
            use_responses_api: options.useResponsesApi,
            overwrite_existing: options.overwriteExisting,
            prepare_brief: options.prepareBrief,
            brief_image_count: options.briefImageCount,
            editor_pass: options.editorPass,
          }
        : undefined

      // Start generation
      const result = await generateProjectPage(galleryId, user.id, apiOptions)
      setAgentId(result.agent_id ?? null)
      setRequestId(result.request_id ?? null)

      // If we got an immediate success with a project page ID, return it.
      if (result.success && result.project_page_id) return result

      // If there is no agent_id, we can't poll status.
      if (!result.agent_id) return result

      // Poll for completion
      const agentIdForPoll = result.agent_id
      let pollCount = 0
      const pollIntervalMs = 2000
      const maxPolls = 450 // 15 minutes max (450 * 2000ms)

      while (pollCount < maxPolls) {
        if (pollCount > 0) {
          await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
        }

        const statusResult = await getAgentStatus(agentIdForPoll)
        if (!statusResult) {
          pollCount++
          continue
        }

        setStatus(statusResult)
        setProgress(statusResult.progress)
        onProgress?.(statusResult)

        if (statusResult.status === 'complete') {
          return {
            success: true,
            project_page_id: statusResult.result?.projectPageId,
            slug: statusResult.result?.slug,
            preview_url: statusResult.result?.previewUrl,
            quality_score: statusResult.result?.qualityScore,
            warnings: statusResult.result?.warnings,
          }
        }

        if (statusResult.status === 'error') {
          return {
            success: false,
            error: statusResult.error || 'Generation failed',
          }
        }

        pollCount++
      }

      return {
        success: false,
        error: 'Generation timed out. Check the project pages list for results.',
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate queries that might show the new project page
        queryClient.invalidateQueries({ queryKey: ['project-pages'] })
        queryClient.invalidateQueries({ queryKey: ['galleries'] })
        onSuccess?.(result)
      } else {
        setError(result.error || 'Generation failed')
        onError?.(result.error || 'Generation failed')
      }
    },
    onError: (err) => {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      onError?.(errorMessage)
    },
  })

  const generate = useCallback(
    async (galleryId: number, options?: GenerateOptions) => {
      return mutation.mutateAsync({ galleryId, options })
    },
    [mutation]
  )

  return {
    generate,
    isLoading: mutation.isPending,
    status,
    agentId,
    requestId,
    progress,
    error,
    reset,
  }
}

/**
 * Check if a gallery is ready for project page generation
 *
 * @param gallery - Gallery object with enrichment status
 * @returns Object with readiness status and reason if not ready
 */
export function checkGalleryReadiness(gallery: {
  id: number
  review_status?: string | null
  job_id?: string | null
  cover_image?: string | null
}): { ready: boolean; reason?: string } {
  // Must have a job ID (for enrichment)
  if (!gallery.job_id) {
    return {
      ready: false,
      reason: 'Gallery is not linked to a Jobber job. Please link a job first.',
    }
  }

  // Review status should be approved (optional check)
  if (gallery.review_status && gallery.review_status !== 'approved') {
    return {
      ready: false,
      reason: 'Gallery must be approved before creating a project page.',
    }
  }

  return { ready: true }
}
